import { ChevronRight } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import { categories } from '../data/fixtures';
import { PageSection, SectionHeader, CategoryTile } from '../shell/ui';

export function BrowseScreen() {
  const { navigate } = useRouter();

  return (
    <>
      {/* Header band */}
      <section
        style={{
          background: 'var(--color-background-surface)',
          borderBottom: '1px solid var(--color-border-light)',
          padding: '3.5rem 0 3rem',
        }}
      >
        <div
          style={{
            maxWidth: '1240px',
            margin: '0 auto',
            padding: '0 1.5rem',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1.5rem',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: 'var(--color-accent-rosegold)',
                fontWeight: 500,
                marginBottom: '0.875rem',
              }}
            >
              All categories
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: 'var(--color-text-primary)',
                margin: 0,
                maxWidth: '720px',
              }}
            >
              Every trade, every studio, <span style={{ color: 'var(--color-accent-rosegold)' }}>all in one place</span>.
            </h1>
          </div>
          <div
            style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
              maxWidth: '360px',
              lineHeight: 1.55,
            }}
          >
            {categories.length} categories · {categories.reduce((a, c) => a + c.serviceCount, 0)} services ·{' '}
            vetted Baku vendors
          </div>
        </div>
      </section>

      {/* Big-tile grid — first 4 as hero row */}
      <PageSection paddingY="4rem">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr 1fr',
            gap: '1.25rem',
            marginBottom: '1.25rem',
          }}
          className="browse-hero-row"
        >
          <CategoryTile
            name={categories[0].name}
            tagline={categories[0].tagline}
            image={categories[0].image}
            serviceCount={categories[0].serviceCount}
            onClick={() => navigate('category', { categoryId: categories[0].id })}
            size="lg"
          />
          <CategoryTile
            name={categories[1].name}
            tagline={categories[1].tagline}
            image={categories[1].image}
            serviceCount={categories[1].serviceCount}
            onClick={() => navigate('category', { categoryId: categories[1].id })}
            size="md"
          />
          <CategoryTile
            name={categories[2].name}
            tagline={categories[2].tagline}
            image={categories[2].image}
            serviceCount={categories[2].serviceCount}
            onClick={() => navigate('category', { categoryId: categories[2].id })}
            size="md"
          />
        </div>

        {/* Remaining as a small grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1.25rem',
          }}
          className="browse-rest-grid"
        >
          {categories.slice(3).map((c) => (
            <CategoryTile
              key={c.id}
              name={c.name}
              tagline={c.tagline}
              image={c.image}
              serviceCount={c.serviceCount}
              onClick={() => navigate('category', { categoryId: c.id })}
              size="sm"
            />
          ))}
        </div>

        <style>{`
          @media (max-width: 980px) {
            .browse-hero-row { grid-template-columns: 1fr 1fr !important; }
            .browse-rest-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
          @media (max-width: 720px) {
            .browse-hero-row { grid-template-columns: 1fr !important; }
            .browse-rest-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
        `}</style>
      </PageSection>

      {/* Index list (editorial) */}
      <PageSection background="var(--color-background-surface)" paddingY="4rem">
        <SectionHeader
          eyebrow="A–Z index"
          title="Or jump straight in."
          description="Every category, with the number of verified vendors currently taking bookings."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0 3rem',
            background: 'var(--color-background-card)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            padding: '0.5rem 2rem',
          }}
          className="browse-index"
        >
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate('category', { categoryId: c.id })}
              style={{
                all: 'unset',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1.125rem 0',
                borderBottom: '1px solid var(--color-border-light)',
              }}
              className="browse-index-row"
            >
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontSize: '1.125rem',
                    fontWeight: 400,
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {c.name}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  {c.subcategories.map((s) => s.name).join(' · ')}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                }}
              >
                <span>{c.serviceCount}</span>
                <ChevronRight size={16} />
              </div>
            </button>
          ))}
        </div>
        <style>{`
          @media (max-width: 760px) {
            .browse-index { grid-template-columns: 1fr !important; padding: 0.5rem 1.25rem !important; }
          }
          .browse-index-row:hover { color: var(--color-accent-rosegold) !important; }
        `}</style>
      </PageSection>
    </>
  );
}
